/**
 * `helper/customerHelpers` — WEB variant. Mirrors the SOURCE FILE, seeded with only the
 * symbols needed so far (plan §5.4).
 *
 * ⚠️ **`parseCustomerRecord` MOVED to `./shared` on 2026-09-05**, together with mobile's
 * `filterAddresses`, when the two `CustomerSlice` variants converged on one `getCustomerRecord`.
 * It is still exported from here by the `export *` below, so no importer changed. The header
 * used to say the two symbols were permanently disjoint — that was true of the SYMBOLS, never
 * of the module, and it stopped being true the day both slices needed both.
 *
 * What is left here is genuinely web's: `getMaskedCustomerNumber`, whose
 * precedence is the opposite of mobile's; and `fetchDetailedCustomerInfo`, which mobile answers
 * precedence is the opposite of mobile's.
 *
 * ⚠️ `fetchDetailedCustomerInfo` is NOT here — it is in `./shared`, because `getDetailedCustomerInfo`
 * is a SHARED reducer and the dispatch has no platform half. (Mobile's app happens to have a
 * differently-shaped function of the same name in `components/DeliveryDinein/deliveryModalHelpers`
 * — it takes a customer object, an offline flag and a callback, not a ref id. A different
 * function, not this one's twin, and not a reason to split this one.)
 *
 * Everything else in the apps' `customerHelpers` stays put for now. Most of it reads the
 * app's redux `store` singleton — that used to block a core-side move, but `packages/core`
 * now owns the real store singleton itself (`../../redux-store`), which is what let
 * `fetchDetailedCustomerInfo` move here; the remaining functions just haven't been asked for.
 */
import store from "../../redux-store";
// Imported AS WELL AS re-exported: this file calls it below, and a re-export creates no local
// binding (ledger hazard 30).
import { maskPhoneNumber } from "./shared";

export * from "./shared";   // rule 4: the folder always has all three files


/**
 * The masked phone number for the customer on this bill — WEB's precedence.
 *
 * 🔴 **The two platforms read the SAME two slices in the OPPOSITE order**, and that is
 * transcribed, not a bug to collapse: web prefers the number written onto the SALE
 * (`saleDataStore.saleData.customerInfo`) and falls back to the searched-customer record, while
 * mobile prefers the record. They differ whenever a cashier attaches one customer and then
 * searches another without attaching — web keeps showing the attached one. Unifying them would
 * change which number one platform's OTP screen claims to have texted.
 *
 * The masking itself is shared — see `maskPhoneNumber`.
 */
export function getMaskedCustomerNumber(): string {
  const state: any = store.getState();
  const customerInfo =
    state?.saleDataStore?.saleData?.customerInfo || state?.customerStore?.customerInfo;
  return maskPhoneNumber(customerInfo?.contactInfo?.phoneNumber || "");
}
